/**
 * Offline write queue. Writes are recorded as operations and replayed in order when the
 * network is back. Pure queue logic; persistence and the actual Supabase calls live in
 * src/data/sync.ts.
 */

import { awardFromLegacyReason, type AwardReason } from './xp-award.ts';

/**
 * An XP award request for the server's award_xp(). `amount` and `day` only drive the
 * optimistic local total until the next refresh; the server decides what is granted.
 */
export interface XpRow {
  reason: AwardReason;
  ref: string;
  amount: number;
  day: string;
}

export interface ProgressRow {
  lesson_id: string;
  status: 'started' | 'done';
  steps: unknown;
  best_accuracy: number | null;
  completed_at: string | null;
}

export interface CardRow {
  id: string;
  kind: 'word' | 'sentence' | 'grammar' | 'mistake';
  ref_id: string;
  lang: string;
  front: unknown;
  back: unknown;
  due: string;
  stability: number;
  difficulty: number;
  elapsed_days: number;
  scheduled_days: number;
  reps: number;
  lapses: number;
  state: number;
  last_review: string | null;
}

export interface ReviewLogRow {
  card_id: string;
  rating: number;
  state: number;
  elapsed_days: number;
  scheduled_days: number;
  reviewed_at: string;
}

export type Op =
  | { type: 'xp'; row: XpRow }
  | { type: 'progress'; row: ProgressRow }
  | { type: 'card'; row: CardRow }
  | { type: 'review'; row: ReviewLogRow };

/** Key identifying ops that supersede each other (latest state wins). */
function supersedeKey(op: Op): string | null {
  switch (op.type) {
    case 'progress':
      return `progress:${op.row.lesson_id}`;
    case 'card':
      return `card:${op.row.id}`;
    case 'xp':
    case 'review':
      return null;
  }
}

/**
 * Appends `op`. A newer progress or card snapshot replaces the queued older one in place,
 * keeping its position so that a card row is still written before its review-log rows.
 */
export function enqueue(queue: readonly Op[], op: Op): Op[] {
  const key = supersedeKey(op);
  if (key !== null) {
    const index = queue.findIndex((q) => supersedeKey(q) === key);
    if (index !== -1) return queue.map((q, i) => (i === index ? op : q));
  }
  return [...queue, op];
}

/** Removes the first `count` ops after they were written successfully. */
export function dropFlushed(queue: readonly Op[], count: number): Op[] {
  return queue.slice(count);
}

function isOp(op: unknown): op is Op {
  return (
    typeof op === 'object' &&
    op !== null &&
    ['xp', 'progress', 'card', 'review'].includes((op as { type?: string }).type ?? '') &&
    typeof (op as { row?: unknown }).row === 'object' &&
    (op as { row?: unknown }).row !== null
  );
}

/**
 * Older versions queued XP as direct inserts (`reason` = `exercise:<lesson>:<step>` or
 * `review:<card>`, no `ref`). Those become award requests; anything unconvertible is dropped.
 */
function upgrade(op: Op): Op | null {
  if (op.type !== 'xp' || typeof (op.row as Partial<XpRow>).ref === 'string') return op;
  const award = awardFromLegacyReason(String((op.row as { reason?: unknown }).reason ?? ''));
  return award ? { type: 'xp', row: { ...op.row, ...award } } : null;
}

export function parseQueue(raw: string | null): Op[] {
  if (!raw) return [];
  try {
    const value: unknown = JSON.parse(raw);
    if (!Array.isArray(value)) return [];
    return value.filter(isOp).flatMap((op) => upgrade(op) ?? []);
  } catch {
    return [];
  }
}
